export class TrackingProviderDetector {
    /**
     * Detect tracking provider based on URL pattern
     * @param url - Tracking URL
     * @returns 'livepin' | 'gpstrack' | 'unknown'
     */
    static detectProvider(url: string): 'livepin' | 'gpstrack' | 'unknown' {
        if (!url || typeof url !== 'string') {
            return 'unknown';
        }

        // Check for LivePin patterns
        if (url.includes('livepin.in') || url.includes('api.livepin.in')) {
            return 'livepin';
        }

        // Check for GPS Track patterns
        if (url.includes('gpstrack.in') || url.includes('app.gpstrack.in')) {
            return 'gpstrack';
        }

        return 'unknown';
    }

    /**
     * Check if URL is a valid tracking URL
     * @param url - URL to validate
     * @returns boolean
     */
    static isValidTrackingUrl(url: string): boolean {
        return this.detectProvider(url) !== 'unknown';
    }

    /**
     * Get provider-specific validation
     * @param url - URL to validate
     * @returns object with validation results
     */
    static validateUrl(url: string): {
        isValid: boolean;
        provider: 'livepin' | 'gpstrack' | 'unknown';
        message: string;
    } {
        const provider = this.detectProvider(url);

        switch (provider) {
            case 'livepin':
                return {
                    isValid: this.isValidLivePinUrl(url),
                    provider: 'livepin',
                    message: this.isValidLivePinUrl(url)
                        ? 'Valid LivePin URL'
                        : 'Invalid LivePin URL format'
                };

            case 'gpstrack':
                return {
                    isValid: this.isValidGpsTrackUrl(url),
                    provider: 'gpstrack',
                    message: this.isValidGpsTrackUrl(url)
                        ? 'Valid GPS Track URL'
                        : 'Invalid GPS Track URL format'
                };

            default:
                return {
                    isValid: false,
                    provider: 'unknown',
                    message: 'Unknown tracking provider'
                };
        }
    }

    /**
     * Validate LivePin URL format
     * @param url - URL to validate
     * @returns boolean
     */
    private static isValidLivePinUrl(url: string): boolean {
        const livePinPattern = /^https:\/\/api\.livepin\.in\/api\/share-links\/[a-zA-Z0-9\-]+$/;
        return livePinPattern.test(url);
    }

    /**
     * Validate GPS Track URL format
     * @param url - URL to validate
     * @returns boolean
     */
    private static isValidGpsTrackUrl(url: string): boolean {
        const gpsTrackPattern = /^https:\/\/(app\.)?gpstrack\.in\/api\/get_current_data/;
        return gpsTrackPattern.test(url);
    }
}
